import { PessoaDataType } from "@/types/company"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { motion } from "framer-motion"

interface UserCardProps {
  user: PessoaDataType
  onEdit: (user: PessoaDataType) => void
}

export function UserCard({ user, onEdit }: UserCardProps) {
  const getInitials = (name: string | undefined) => {
    if (!name) return "U"
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase()
  }

  return (
    <motion.div
      onClick={() => onEdit(user)}
      whileHover={{ scale: 1.01, backgroundColor: "rgba(39, 39, 42, 0.5)" }}
      transition={{ duration: 0.2 }}
      className="group relative cursor-pointer overflow-hidden rounded-xl border border-white/15 bg-zinc-900/30 p-5 shadow-sm"
    >
      {/* Indicador lateral sutil no hover */}
      <div className="absolute left-0 top-0 h-full w-1 bg-orange-500 opacity-0 transition-opacity group-hover:opacity-100" />

      <div className="flex items-center gap-4">
        {/* Avatar menor e mais integrado */}
        <div className="relative flex-shrink-0">
          <Avatar className="h-14 w-14 border border-white/10 shadow-lg">
            <AvatarImage
              className="object-cover"
              src={user.pes_foto_url || undefined}
            />
            <AvatarFallback className="bg-zinc-800 text-xs font-bold text-zinc-500">
              {getInitials(user.pes_nome)}
            </AvatarFallback>
          </Avatar>
          {/* Dot de Status sobre o Avatar */}
          <span
            className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-zinc-900 ${user.pes_status ? "bg-emerald-500" : "bg-red-500"}`}
          />
        </div>

        {/* Informações alinhadas à esquerda para reduzir espaço sobrando */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="truncate text-base font-bold tracking-tight text-zinc-100 transition-colors group-hover:text-orange-500">
            {user.pes_nome}
          </h3>
          <p className="truncate text-xs text-zinc-500">{user.pes_email}</p>

          <div className="mt-2 flex items-center gap-2">
            <Badge
              variant="outline"
              className={`h-5 border-none px-2 text-[10px] font-semibold uppercase tracking-wider ${
                user.pes_status
                  ? "bg-emerald-500/10 text-emerald-500"
                  : "bg-red-500/10 text-red-500"
              }`}
            >
              {user.pes_status ? "Ativo" : "Inativo"}
            </Badge>
            <Badge
              variant="secondary"
              className="h-5 border-none bg-zinc-800/50 px-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
            >
              {user.perfil || "Colaborador"}
            </Badge>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
